import { memo, useCallback, useEffect, useMemo, useRef, useSyncExternalStore, RefObject } from 'react';
import { isSplitsInTimelineEnabledPure } from '@goldcast/player-config/src/utils/clipUtils';
import { getChapterPlayerTime, getChapterSegmentTime } from './utils';
import { useRemotionPlayerSeek } from '@/App/remotion/MainPlayer/ClipPlayerV2WithNewTimeline/hooks/useRemotionPlayerSeek';
import { useRemotionPlayerCurrentTime } from '@/App/remotion/MainPlayer/ClipPlayerV2WithNewTimeline/hooks/useRemotionPlayerCurrentTime';
import { useClipsContext } from '@/context/ClipsContext/ClipsContext';
import featureFlagStore from '@/stores/featureFlagStore';
import { FeatureFlagKeys } from '@/services/featureFlag';
import { useChapterParagraphs } from '@/components/molecules/Transcript/useChapterParagraphs';
import EventBus from '@/libs/eventBus/eventBus';
import { CustomEvents } from '@/libs/eventBus/constants';
import { formatTime } from '@/libs/core';
import Icon from '@/components/atoms/Icon';

interface ChaptersMenuPopoverProps {
  onClose: () => void;
  buttonRef: RefObject<HTMLDivElement>;
}

const ChaptersMenuPopover = ({ onClose, buttonRef }: ChaptersMenuPopoverProps) => {
  const popoverRef = useRef<HTMLDivElement>(null);
  const { seekTo } = useRemotionPlayerSeek();
  const { currentTime } = useRemotionPlayerCurrentTime();
  const { clipData } = useClipsContext();
  const featureFlags = useSyncExternalStore(featureFlagStore.subscribe, featureFlagStore.getSnapshot);

  const isSplitsTranslationEnabled = !!featureFlags[FeatureFlagKeys.Use_CL_Splits_Translations];

  const isSplitsEnabled = useMemo(
    () => isSplitsInTimelineEnabledPure({ clip: clipData, isSplitsTranslationEnabled }),
    [clipData, isSplitsTranslationEnabled]
  );

  const chapters = useChapterParagraphs();

  const chapterDisplayTimes = useMemo(() => {
    return chapters.map(chapter => getChapterPlayerTime(chapter, clipData, isSplitsEnabled));
  }, [chapters, clipData, isSplitsEnabled]);

  const activeChapterIndex = useMemo(() => {
    let active = -1;
    for (let i = 0; i < chapterDisplayTimes.length; i++) {
      if ((chapterDisplayTimes[i] ?? 0) <= currentTime) {
        active = i;
      }
    }
    return active;
  }, [chapterDisplayTimes, currentTime]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onClose, buttonRef]);

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [onClose]);

  const handleChapterClick = useCallback(
    (index: number) => {
      const chapter = chapters[index];
      if (!chapter) {
        return;
      }

      const playerTime = getChapterPlayerTime(chapter, clipData, isSplitsEnabled);
      const segmentTime = getChapterSegmentTime(chapter, clipData, isSplitsEnabled);

      EventBus.dispatch(CustomEvents.TimelineForcedScroll, {
        time: segmentTime,
        segmentId: chapter.segmentId
      });

      seekTo(playerTime, false);
      onClose();
    },
    [chapters, isSplitsEnabled, clipData, seekTo, onClose]
  );

  return (
    <div
      ref={popoverRef}
      data-testid="chapters-menu-popover"
      className="absolute bottom-full z-[9999] mb-3 flex max-h-[312px] w-[320px] flex-col overflow-hidden rounded-2xl bg-white shadow-[0px_4px_16px_rgba(0,0,0,0.25)]"
      role="menu"
      aria-label="Chapters"
    >
      <div className="flex shrink-0 items-center justify-between p-4">
        <h2 className="text-base font-medium text-slate-950">Chapters</h2>
        <button
          onClick={onClose}
          className="rounded-md p-1 text-gray-900 hover:bg-gray-200"
          aria-label="Close chapters"
        >
          <Icon name="IconX" size={16} />
        </button>
      </div>

      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
        {chapters.map((chapter, index) => (
          <button
            key={chapter.id}
            data-testid={`chapter-menu-item-${chapter.id}`}
            className={`flex flex-col rounded-md px-4 py-[6px] text-left ${activeChapterIndex === index ? 'bg-gray-100 hover:bg-gray-100' : 'hover:bg-gray-50'}`}
            onClick={() => handleChapterClick(index)}
            role="menuitem"
          >
            <span className="text-sm font-medium text-black">{chapter.title}</span>
            <span className="text-xs leading-[18px] text-neutral-500">
              {formatTime(chapterDisplayTimes[index] ?? chapter.start)}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
};

export default memo(ChaptersMenuPopover);
